import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { format, addDays, subDays } from "date-fns";
import { tr } from "date-fns/locale";
import {
  ChevronLeft, ChevronRight, Ship, Layers, Users, RefreshCw,
  AlertTriangle, Check, X, Info, Music, Wine, TreePalm, Bath,
  Calendar, Anchor, GripVertical, Clock, Sparkles, Move, Merge, RotateCcw, Crown, DownloadCloud,
  Minus, Plus, Maximize2
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import RealTable from "../components/RealTable";
import { useFloorFit } from "../hooks/useFloorFit";

const API = window.location.origin;

// Style C — Dark Slate Operations Board zone palette
const ZONES = [
  { value: "cam_kenari", label: "Cam Kenarı", color: "#06B6D4", bg: "rgba(6,182,212,0.15)", glow: "0 0 15px rgba(6,182,212,0.35)", badgeBg: "#164E63", badgeText: "#A5F3FC" },
  { value: "sahne_onu", label: "Sahne Önü", color: "#F97316", bg: "rgba(249,115,22,0.15)", glow: "0 0 15px rgba(249,115,22,0.35)", badgeBg: "#7C2D12", badgeText: "#FFEDD5" },
  { value: "vip_lounge", label: "VIP Lounge", color: "#F59E0B", bg: "rgba(245,158,11,0.15)", glow: "0 0 15px rgba(245,158,11,0.35)", badgeBg: "#78350F", badgeText: "#FDE68A" },
  { value: "standart", label: "Standart", color: "#64748B", bg: "rgba(100,116,139,0.15)", glow: "none", badgeBg: "#334155", badgeText: "#E2E8F0" },
];
const getZone = (v) => ZONES.find(z => z.value === v) || ZONES[3];

// Şema Editörü ile AYNI mantıksal tuval boyutu (masa x/y bu alana konumlanır)
const WLOGICAL = 1560, HLOGICAL = 1900;

export default function TableOperations() {
  const [date, setDate] = useState(new Date());
  const [session] = useState("dinner");
  const [boats, setBoats] = useState([]);
  const [selectedBoat, setSelectedBoat] = useState(null);
  const [floors, setFloors] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [tables, setTables] = useState([]);
  const [elements, setElements] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [waiting, setWaiting] = useState([]);
  const [missingTables, setMissingTables] = useState([]);
  const [ensuring, setEnsuring] = useState(false);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [dropTarget, setDropTarget] = useState(null);
  const [validationModal, setValidationModal] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const dateStr = format(date, "yyyy-MM-dd");
  const dateDisplay = format(date, "d MMMM yyyy, EEEE", { locale: tr });
  const token = localStorage.getItem("auth_token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const boatsRes = await axios.get(`${API}/api/tm/boats`, { headers, timeout: 10000 });
      const realBoats = boatsRes.data?.boats || [];
      setBoats(realBoats);
      const activeBoat = selectedBoat || (realBoats.length > 0 ? realBoats[0].id : null);
      if (!selectedBoat && activeBoat) setSelectedBoat(activeBoat);

      const boatParam = activeBoat ? `?boat_id=${activeBoat}` : "";
      const opRes = await axios.get(`${API}/api/tm/operation/${dateStr}/${session}${boatParam}`, { headers, timeout: 15000 });
      const d = opRes.data;
      setFloors(d.floors || []);
      setTables(d.tables || []);
      setElements(d.elements || []);
      setAssignments(d.assignments || []);
      setWaiting(d.waiting || []);
      setMissingTables(d.missing_tables || []);
      setStats(d.stats || {});
      setLastUpdate(new Date());

      const bFloors = (d.floors || []).filter(f => f.boat_id === activeBoat);
      if (bFloors.length > 0) {
        setSelectedFloor(prev => (bFloors.find(f => f.id === prev) ? prev : bFloors[0].id));
      }
    } catch (err) { console.error("Fetch error:", err); }
    setLoading(false);
  }, [dateStr, session, selectedBoat]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  // Dinamik bölgeleri seçili kata göre getir
  useEffect(() => {
    if (!selectedFloor) { setLanes([]); return; }
    axios.get(`${API}/api/tm/lanes/${selectedFloor}`, { headers })
      .then(r => setLanes(r.data?.lanes || []))
      .catch(() => setLanes([]));
  }, [selectedFloor, tables]); // eslint-disable-line react-hooks/exhaustive-deps

  const floorLanes = lanes.filter(l => l.floor_id === selectedFloor);
  const zonePax = (laneId) => {
    const tIds = tables.filter(t => t.lane_id === laneId).map(t => t.id);
    const bset = new Set(assignments.filter(a => tIds.includes(a.table_id)).map(a => a.booking_id));
    let sum = 0;
    bset.forEach(bid => {
      const asg = assignments.find(a => a.booking_id === bid && tIds.includes(a.table_id));
      sum += (asg?.booking?.total_pax) || (tables.find(t => t.id === asg?.table_id)?.capacity) || 0;
    });
    return sum;
  };

  const seatInZone = async (booking, laneId, force = false) => {
    try {
      const r = await axios.post(`${API}/api/tm/zones/${laneId}/seat`,
        { booking_id: booking.id, date: dateStr, session, force }, { headers });
      if (r.data?.seated) {
        toast.success(`${booking.customer_name || "Rezervasyon"} → ${r.data.table?.number} (bölgeye eklendi)`);
        if (r.data.warning_msg) toast.warning(r.data.warning_msg);
        setPickBooking(null);
        fetchData(true);
      } else if (r.data?.warning) {
        if (window.confirm(r.data.warning + "\nYine de eklensin mi?")) seatInZone(booking, laneId, true);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Bölgeye eklenemedi"); }
  };
  const seatInZoneRef = useRef(null);
  useEffect(() => { seatInZoneRef.current = seatInZone; });

  const boatFloors = floors.filter(f => f.boat_id === selectedBoat);
  const currentFloor = floors.find(f => f.id === selectedFloor);
  const floorTables = tables.filter(t => t.floor_id === selectedFloor);
  const floorElements = elements.filter(e => e.floor_id === selectedFloor);

  // Masa PAYLAŞIMI: bir masada birden fazla rezervasyon -> table_id -> [assignment,...]
  const tableAssignMap = {};
  assignments.forEach(a => { (tableAssignMap[a.table_id] = tableAssignMap[a.table_id] || []).push(a); });

  const [drag, setDrag] = useState(null); // {booking, fromTableId, fromAssignmentId, x, y, active}
  const [pickBooking, setPickBooking] = useState(null); // tıkla-yerleştir için seçili rezervasyon
  const [needFilter, setNeedFilter] = useState(""); // bekleme listesi ihtiyaç filtresi

  // İhtiyaç anahtarı -> rozet etiketleri (backend _tm_option_tags ile aynı)
  const NEED_TAGS = { cam_kenari: ["Cam Kenarı", "VIP Cam Kenarı"], sahne_onu: ["Sahne Önü"], vip_lounge: ["VIP Lounge"], pvt: ["Özel Masa"] };
  const NEED_LABEL = { cam_kenari: "Cam", sahne_onu: "Sahne", vip_lounge: "Lounge", pvt: "Özel" };
  const hasNeed = (tags, key) => (tags || []).some(tg => NEED_TAGS[key].includes(tg.label));
  // Bölgede ihtiyaç bazında KULLANILAN masa sayısı (kontenjan = masa adedi)
  const zoneNeedUsed = (laneId, key) => {
    const tIds = new Set(tables.filter(t => t.lane_id === laneId).map(t => t.id));
    const used = new Set();
    assignments.forEach(a => { if (tIds.has(a.table_id) && hasNeed(a.booking?.tags, key)) used.add(a.table_id); });
    return used.size;
  };
  const waitingShown = needFilter ? waiting.filter(w => hasNeed(w.tags, needFilter)) : waiting;
  const lastDragActiveRef = useRef(false);
  const dragRef = useRef(null);
  useEffect(() => { dragRef.current = drag; }, [drag]);
  const assignmentsRef = useRef([]);
  useEffect(() => { assignmentsRef.current = assignments; }, [assignments]);
  const tableAssignMapRef = useRef({});
  tableAssignMapRef.current = tableAssignMap;
  const tablesRef = useRef([]);
  useEffect(() => { tablesRef.current = tables; }, [tables]);
  const wsRef = useRef(null);
  const wsOpenRef = useRef(false);
  const liveRefetchRef = useRef(null);
  const [liveState, setLiveState] = useState({ connected: false, lastSync: null });
  // Canlı Defter'de (operation_rows) bir değişiklik olunca panoyu 1.2 sn içinde sessizce yeniden yükle (debounce)
  const scheduleLiveRefetch = useCallback(() => {
    clearTimeout(liveRefetchRef.current);
    liveRefetchRef.current = setTimeout(() => { fetchData(true); setLiveState(p => ({ ...p, lastSync: new Date() })); }, 1200);
  }, [fetchData]);

  // ---- Zoom / Sığdır (Şema Editörü ile AYNI ölçekleme; ortak hook) ----
  const { zoom, viewportRef, zoomBy, resetZoom, fitToView } = useFloorFit(
    floorTables, floorElements, !!selectedFloor, `${selectedFloor}-${tables.length}-${elements.length}`
  );

  // ---- Pointer/touch drag başlat (fare + iPad) ----
  const startDrag = (e, booking, fromTableId = null, fromAssignmentId = null) => {
    if (e.button === 2) return;
    const pt = e.touches ? e.touches[0] : e;
    setDrag({ booking, fromTableId, fromAssignmentId, x: pt.clientX, y: pt.clientY, startX: pt.clientX, startY: pt.clientY, active: false });
  };

  // Masa paylaşımı sayesinde her bırakma tek bir "ata" çağrısıdır (backend eskiyi taşır, hedefe ekler)
  const doDrop = async (d, targetTableId) => {
    if (!d || !targetTableId || d.fromTableId === targetTableId) return;
    try {
      const res = await axios.post(`${API}/api/tm/assignments`, { booking_id: d.booking.id, table_id: targetTableId, date: dateStr, session, force: false }, { headers });
      if (res.data.assigned === false && res.data.validation) {
        setValidationModal({ booking: d.booking, tableId: targetTableId, validation: res.data.validation });
      } else if (res.data.assigned && res.data.occupancy?.overfull) {
        const tnum = (tablesRef.current.find(t => t.id === targetTableId) || {}).number;
        toast.warning(`Masa ${tnum} kapasite aşımı: ${res.data.occupancy.total}/${res.data.occupancy.capacity} kişi`);
      }
      if (!wsOpenRef.current) fetchData(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "İşlem hatası");
    }
  };

  // ---- Tıkla-yerleştir: rezervasyon seç, sonra masaya tıkla ----
  const togglePick = (w) => {
    if (lastDragActiveRef.current) return; // sürükleme sonrası click'i yok say
    setPickBooking(p => (p && p.id === w.id) ? null : w);
  };
  const seatPickedOnTable = (tableId) => {
    if (!pickBooking) return;
    doDrop({ booking: pickBooking, fromTableId: null }, tableId);
    const tnum = (tablesRef.current.find(t => t.id === tableId) || {}).number;
    toast.success(`${pickBooking.customer_name || "Rezervasyon"} → Masa ${tnum}`);
    setPickBooking(null);
  };

  // Global pointer/touch dinleyiciler (bir kez bağlanır, eşiği geçince aktif olur -> liste kaydırma korunur)
  useEffect(() => {
    const getTableAt = (x, y) => {
      const el = document.elementFromPoint(x, y);
      const t = el && el.closest ? el.closest('[data-tableid]') : null;
      return t ? parseInt(t.getAttribute('data-tableid')) : null;
    };
    const getZoneAt = (x, y) => {
      const el = document.elementFromPoint(x, y);
      const z = el && el.closest ? el.closest('[data-zoneid]') : null;
      return z ? parseInt(z.getAttribute('data-zoneid')) : null;
    };
    const move = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const pt = e.touches ? e.touches[0] : e;
      if (!d.active) {
        const dist = Math.hypot(pt.clientX - d.startX, pt.clientY - d.startY);
        if (dist < 8) return;  // eşik altı -> kaydırmaya izin ver
        setDrag(dd => dd ? { ...dd, active: true, x: pt.clientX, y: pt.clientY } : dd);
        dragRef.current = { ...d, active: true };
        return;
      }
      if (e.cancelable) e.preventDefault();
      setDrag(dd => dd ? { ...dd, x: pt.clientX, y: pt.clientY } : dd);
      setDropTarget(getTableAt(pt.clientX, pt.clientY));
    };
    const up = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const pt = e.changedTouches ? e.changedTouches[0] : e;
      const target = d.active ? getTableAt(pt.clientX, pt.clientY) : null;
      const zoneTarget = (d.active && !target) ? getZoneAt(pt.clientX, pt.clientY) : null;
      lastDragActiveRef.current = d.active; // gerçek sürükleme mi? (click'i bastırmak için)
      setDrag(null); setDropTarget(null);
      if (target) doDrop(d, target);
      else if (zoneTarget && d.booking) seatInZoneRef.current && seatInZoneRef.current(d.booking, zoneTarget);
      setTimeout(() => { lastDragActiveRef.current = false; }, 60);
    };
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- WS canlı delta: çoklu kullanıcıda tam yenileme olmadan anlık güncelleme ----
  const applyTmDelta = useCallback((msg) => {
    const act = msg.action;
    if (act === 'set' && msg.assignment) {
      const a = msg.assignment;
      // Bir rezervasyon tek satır; masa paylaşımında table_id'ye göre FİLTRELEME (birden fazla olabilir)
      setAssignments(prev => [...prev.filter(x => x.booking_id !== a.booking_id), a]);
      setWaiting(prev => prev.filter(w => w.id !== a.booking_id));
    } else if (act === 'move') {
      setAssignments(prev => prev.map(x => x.id === msg.assignment_id ? { ...x, table_id: msg.table_id } : x));
    } else if (act === 'remove') {
      setAssignments(prev => {
        const removed = prev.find(x => x.id === msg.assignment_id || (x.table_id === msg.table_id && x.booking_id === msg.booking_id));
        if (removed && removed.booking) {
          setWaiting(w => w.some(z => z.id === removed.booking_id) ? w : [{ ...removed.booking, id: removed.booking_id, parsed: {}, score: 0 }, ...w]);
        }
        return prev.filter(x => x !== removed);
      });
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    let ws, closed = false, retry;
    const connect = () => {
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const url = `${proto}//${window.location.host}/api/ws/sheet?date=${dateStr}&token=${token}`;
      try { ws = new WebSocket(url); } catch { return; }
      wsRef.current = ws;
      ws.onopen = () => { wsOpenRef.current = true; setLiveState(p => ({ ...p, connected: true })); };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === 'tm_assignment') { applyTmDelta(msg); return; }
          // Canlı Defter olayları: hücre/satır güncelleme, yeni satır, iptal -> pano verisini canlı tut
          if (msg.type === 'cell_update' || msg.type === 'row_update' || msg.type === 'row_new' || msg.type === 'cancel_audit') {
            const keys = Object.keys(msg.fields || {});
            const onlyTmMeta = keys.length > 0 && keys.every(k => k === 'manual_edits' || k === 'cell_colors');
            if (!onlyTmMeta) scheduleLiveRefetch();
          }
        } catch { /* noop */ }
      };
      ws.onclose = () => { wsOpenRef.current = false; setLiveState(p => ({ ...p, connected: false })); if (!closed) retry = setTimeout(connect, 2000); };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };
    connect();
    return () => { closed = true; clearTimeout(retry); clearTimeout(liveRefetchRef.current); try { ws && ws.close(); } catch { /* noop */ } };
  }, [dateStr, token, applyTmDelta, scheduleLiveRefetch]);

  const forceAssign = async () => {
    if (!validationModal) return;
    try {
      if (validationModal.moveId) {
        await axios.put(`${API}/api/tm/assignments/${validationModal.moveId}/move`, { table_id: validationModal.tableId, force: true }, { headers });
      } else {
        await axios.post(`${API}/api/tm/assignments`, { booking_id: validationModal.booking.id, table_id: validationModal.tableId, date: dateStr, session, force: true }, { headers });
      }
      setValidationModal(null);
      if (!wsOpenRef.current) fetchData(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
  };

  const removeAssignment = async (assignmentId) => {
    const removed = assignmentsRef.current.find(a => a.id === assignmentId);
    setAssignments(prev => prev.filter(a => a.id !== assignmentId));
    if (removed?.booking) setWaiting(w => w.some(z => z.id === removed.booking_id) ? w : [{ ...removed.booking, id: removed.booking_id, parsed: {}, score: 0 }, ...w]);
    try { await axios.delete(`${API}/api/tm/assignments/${assignmentId}`, { headers }); } catch (e) { console.error("Atama silme hatası:", e); toast.error("Atama kaldırılamadı"); }
  };

  const syncFromLive = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/api/tm/sync-from-live/${dateStr}/${session}${selectedBoat ? `?boat_id=${selectedBoat}` : ""}`, {}, { headers });
      const { synced = 0, conflicts = 0, unresolved = 0, removed = 0 } = res.data || {};
      if (synced > 0) toast.success(`${synced} rezervasyon Canlı Defter'den yerleştirildi`);
      else toast.info("Canlı Defter ile zaten güncel");
      if (removed > 0) toast.message(`${removed} eski yerleşim kaldırıldı (Canlı Defter'de masa boş/değişmiş)`);
      if (conflicts > 0) toast.warning(`${conflicts} masa çakışması atlandı`);
      if (unresolved > 0) toast.message(`${unresolved} rezervasyonun masası şemada çizili değil`);
      await fetchData(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Senkronizasyon hatası");
    }
    setSyncing(false);
  };

  // Canlı Defter'de yazılı ama şemada olmayan masaları kata ekle (kapasite 0 = 'x'), sonra senkronla
  const ensureMissingTables = async (floorId, numbers) => {
    setEnsuring(true);
    try {
      const r = await axios.post(`${API}/api/tm/floors/${floorId}/ensure-tables`, { numbers, date: dateStr, session }, { headers });
      const { created = [], reactivated = 0, synced = 0 } = r.data || {};
      toast.success(`${created.length + reactivated} masa şemaya eklendi, ${synced} rezervasyon yerleşti`);
      await fetchData(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Masalar eklenemedi (yönetici yetkisi gerekir)");
    }
    setEnsuring(false);
  };
  const floorMissing = missingTables.find(m => m.floor_id === selectedFloor);
  const boatMissingCount = missingTables.filter(m => boatFloors.some(f => f.id === m.floor_id)).reduce((s, m) => s + m.bookings, 0);

  const zoneStats = {};
  ZONES.forEach(z => { zoneStats[z.value] = { total: 0, occupied: 0, capacity: 0, pax: 0 }; });
  floorTables.forEach(t => {
    if (zoneStats[t.zone]) {
      zoneStats[t.zone].total++;
      zoneStats[t.zone].capacity += t.capacity || 4;
      const asgs = tableAssignMap[t.id] || [];
      if (asgs.length) {
        zoneStats[t.zone].occupied++;
        zoneStats[t.zone].pax += asgs.reduce((s, a) => s + (a?.booking?.total_pax || 0), 0);
      }
    }
  });
  const occupiedCount = floorTables.filter(t => (tableAssignMap[t.id] || []).length).length;
  const occupancyPct = floorTables.length > 0 ? Math.round(occupiedCount / floorTables.length * 100) : 0;
  const assignedBookingCount = new Set(assignments.map(a => a.booking_id)).size;

  const renderElement = (el) => {
    const key = `el-${el.id}`;
    const style = { position: "absolute", left: el.x, top: el.y, width: el.width, height: el.height, pointerEvents: "none", zIndex: 2, transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined };
    if (el.element_type === "sahne") {
      return (<div key={key} style={style}><div className="w-full h-full flex items-center justify-center rounded-b-[50%] border-2 border-dashed border-orange-500/60 bg-orange-500/10"><div className="text-center"><Music className="w-5 h-5 text-orange-400 mx-auto mb-0.5" /><span className="text-xs font-bold text-orange-300 tracking-widest uppercase">{el.label || "SAHNE"}</span></div></div></div>);
    }
    if (el.element_type === "bar") {
      return (<div key={key} style={style}><div className="w-full h-full flex items-center justify-center rounded-lg border-2 border-amber-500/60 bg-amber-500/10 gap-1.5"><Wine className="w-4 h-4 text-amber-400" /><span className="text-xs font-bold text-amber-300">{el.label || "BAR"}</span></div></div>);
    }
    if (el.element_type === "wc") {
      return (<div key={key} style={style}><div className="w-full h-full flex items-center justify-center rounded border border-slate-600 bg-slate-700/40 gap-1"><Bath className="w-3 h-3 text-slate-400" /><span className="text-[9px] font-bold text-slate-400">{el.label || "WC"}</span></div></div>);
    }
    if (el.element_type === "plant") {
      return (<div key={key} style={style}><div className="w-full h-full flex items-center justify-center"><TreePalm className="w-5 h-5 text-emerald-400 drop-shadow" /></div></div>);
    }
    if (el.element_type === "zone_label") {
      return (<div key={key} style={style}><div className="w-full h-full flex items-center justify-center"><span className="text-xs font-black tracking-[0.2em] uppercase text-sky-400/60">{el.label || "BÖLGE"}</span></div></div>);
    }
    if (el.element_type === "border") {
      return (<div key={key} style={style}><div className="w-full border-t-2 border-dashed border-sky-500/30" style={{ marginTop: el.height / 2 }} /></div>);
    }
    if (el.element_type === "text") {
      return (<div key={key} style={style}><span className="text-[10px] text-slate-400 font-medium">{el.label}</span></div>);
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="table-operations">
      {/* === HEADER === */}
      <div className="bg-slate-900/90 backdrop-blur-md border-b border-slate-800 sticky top-0 z-40">
        <div className="px-5 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-900/40"><Anchor className="w-5 h-5 text-white" /></div>
              <div>
                <div className="text-[10px] font-bold text-amber-400 uppercase tracking-[0.2em]">Lüfer · Masa Düzeni</div>
                <h1 className="text-xl font-bold tracking-tight text-white -mt-0.5 flex items-center gap-2">Operasyon Panosu
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${liveState.connected ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-slate-800 text-slate-400 border-slate-700"}`}
                    title={liveState.connected ? "Canlı Defter'e bağlı — değişiklikler anında yansır" : "Bağlantı bekleniyor"} data-testid="op-live-indicator">
                    <span className={`w-1.5 h-1.5 rounded-full ${liveState.connected ? "bg-emerald-400 animate-pulse" : "bg-slate-500"}`} />
                    {liveState.connected ? "CANLI" : "BAĞLANIYOR"}{liveState.lastSync ? ` · ${liveState.lastSync.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}` : ""}
                  </span>
                </h1>
              </div>
            </div>

            {/* Date */}
            <div className="flex items-center gap-1 bg-slate-800/60 rounded-lg px-2 py-1 border border-slate-700">
              <button type="button" onClick={() => setDate(d => subDays(d, 1))} className="p-1 hover:bg-slate-700 rounded" data-testid="date-prev"><ChevronLeft className="w-4 h-4 text-slate-400" /></button>
              <Calendar className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-semibold text-slate-200 min-w-[190px] text-center">{dateDisplay}</span>
              <button type="button" onClick={() => setDate(d => addDays(d, 1))} className="p-1 hover:bg-slate-700 rounded" data-testid="date-next"><ChevronRight className="w-4 h-4 text-slate-400" /></button>
            </div>

            {/* KPIs */}
            <div className="flex items-center gap-2">
              <div className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-1.5 flex items-center gap-2" data-testid="kpi-occupancy">
                <div className="relative w-10 h-10">
                  <svg viewBox="0 0 36 36" className="w-10 h-10 -rotate-90">
                    <circle cx="18" cy="18" r="15" fill="none" stroke="#1E293B" strokeWidth="4" />
                    <circle cx="18" cy="18" r="15" fill="none" stroke="#38BDF8" strokeWidth="4" strokeDasharray={`${occupancyPct * 0.942} 100`} strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center"><span className="text-[11px] font-extrabold font-mono text-sky-400">%{occupancyPct}</span></div>
                </div>
                <div><div className="text-[10px] text-slate-500 uppercase tracking-wide">Doluluk</div><div className="text-xs font-mono font-bold text-slate-300">{occupiedCount}/{floorTables.length}</div></div>
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-center" data-testid="kpi-waiting">
                <div className="text-2xl font-extrabold font-mono text-amber-400 leading-none">{waiting.length}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wide mt-0.5">Bekleyen</div>
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-center" data-testid="kpi-seated">
                <div className="text-2xl font-extrabold font-mono text-emerald-400 leading-none">{assignedBookingCount}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wide mt-0.5">Yerleşti</div>
              </div>
              {boatMissingCount > 0 && (
                <div className="bg-red-950/60 border border-red-500/50 rounded-lg px-3 py-2 text-center" data-testid="kpi-live-mismatch" title="Canlı Defter'de masası yazılı ama şemada o masa çizili olmayan rezervasyon">
                  <div className="text-2xl font-extrabold font-mono text-red-300 leading-none">{boatMissingCount}</div>
                  <div className="text-[10px] text-red-400/80 uppercase tracking-wide mt-0.5">Şemada Yok</div>
                </div>
              )}
            </div>
          </div>

          {/* Tabs + selectors */}
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-2">
              <Link to="/table-layout" className="px-4 py-1.5 bg-sky-500 text-white text-sm font-bold rounded-lg shadow-lg shadow-sky-900/40" data-testid="tab-operations">Operasyon</Link>
              <Link to="/table-schema" className="px-4 py-1.5 bg-slate-800 text-slate-300 text-sm font-medium rounded-lg hover:bg-slate-700 transition-colors" data-testid="tab-schema">Şema Editörü</Link>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap justify-end">
              {boats.map(b => (
                <button type="button" key={b.id} onClick={() => setSelectedBoat(b.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${selectedBoat === b.id ? "bg-sky-500/15 text-sky-300 border-sky-500/60 shadow-[0_0_12px_rgba(56,189,248,0.25)]" : "bg-slate-800/50 text-slate-400 border-slate-700 hover:border-slate-600"}`}
                  data-testid={`boat-pill-${b.id}`}>
                  <Ship className="w-3.5 h-3.5" /> {b.name}
                </button>
              ))}
              <div className="w-px h-5 bg-slate-700 mx-1" />
              {boatFloors.map(f => (
                <button type="button" key={f.id} onClick={() => setSelectedFloor(f.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${selectedFloor === f.id ? (f.floor_type === "VIP" ? "bg-amber-500/15 text-amber-300 border-amber-500/60 shadow-[0_0_12px_rgba(245,158,11,0.25)]" : "bg-slate-700 text-slate-100 border-slate-500") : "bg-slate-800/50 text-slate-400 border-slate-700 hover:border-slate-600"}`}
                  data-testid={`floor-pill-${f.id}`}>
                  {f.floor_type === "VIP" ? <Crown className="w-3.5 h-3.5" /> : <Layers className="w-3.5 h-3.5" />} {f.name}
                  {f.floor_type === "VIP" && <span className="text-[8px] px-1 rounded font-bold bg-amber-500/20 text-amber-300">VIP</span>}
                </button>
              ))}
              <div className="w-px h-5 bg-slate-700 mx-1" />
              <button type="button" onClick={syncFromLive} disabled={syncing}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all bg-emerald-500/15 text-emerald-300 border-emerald-500/50 hover:bg-emerald-500/25 disabled:opacity-50"
                data-testid="sync-from-live-btn" title="Canlı Defter'deki masa/kat bilgisini buraya aktar">
                <DownloadCloud className={`w-3.5 h-3.5 ${syncing ? "animate-pulse" : ""}`} /> {syncing ? "Alınıyor…" : "Canlıdan Al"}
              </button>
              <button type="button" onClick={() => fetchData(true)} className="p-2 text-slate-400 hover:text-sky-400 hover:bg-slate-800 rounded-lg transition-colors" data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-16 text-center text-slate-500 flex flex-col items-center gap-3"><RefreshCw className="w-8 h-8 animate-spin text-slate-600" /> Yükleniyor...</div>
      ) : (
        <div className="flex h-[calc(100vh-118px)]">
          {/* === CENTER: Floor Plan Canvas === */}
          <div className="flex-1 relative overflow-hidden">
            {/* Zoom / Sığdır araç çubuğu (Şema Editörü ile aynı, sabit) */}
            {currentFloor && (
              <div className="absolute top-3 right-3 z-30 flex items-center gap-1 bg-slate-900/90 backdrop-blur-md border border-slate-800 rounded-lg px-1.5 py-1 shadow-xl" data-testid="op-canvas-toolbar">
                <button type="button" onClick={() => zoomBy(-0.1)} className="w-6 h-6 rounded text-slate-300 hover:bg-slate-800 flex items-center justify-center" data-testid="op-zoom-out"><Minus className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={resetZoom} className="text-[10px] font-mono font-bold text-slate-300 w-10 hover:text-sky-300" data-testid="op-zoom-reset">{Math.round(zoom * 100)}%</button>
                <button type="button" onClick={() => zoomBy(0.1)} className="w-6 h-6 rounded text-slate-300 hover:bg-slate-800 flex items-center justify-center" data-testid="op-zoom-in"><Plus className="w-3.5 h-3.5" /></button>
                <div className="w-px h-4 bg-slate-700 mx-0.5" />
                <button type="button" onClick={fitToView} title="Tümünü sığdır"
                  className="px-2 py-1 rounded text-[10px] font-bold text-amber-300 hover:bg-amber-500/10 flex items-center gap-1" data-testid="op-fit-view"><Maximize2 className="w-3.5 h-3.5" /> Sığdır</button>
              </div>
            )}
          <div ref={viewportRef} className="w-full h-full overflow-auto p-5">
            {floorMissing && (
              <div className="mb-3 mx-auto max-w-[920px] flex items-center justify-between gap-3 bg-red-500/10 border border-red-500/40 rounded-lg px-4 py-2" data-testid="missing-tables-banner">
                <div className="text-sm text-red-200 flex items-start gap-2 min-w-0">
                  <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <b>{floorMissing.bookings} rezervasyon</b> Canlı Defter'de bu kata yazılı ama masası şemada çizili değil:
                    <span className="ml-1 font-mono text-red-100 break-words" data-testid="missing-tables-numbers">{floorMissing.numbers.join(", ")}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link to="/table-schema" className="text-xs font-semibold text-slate-300 hover:text-white bg-slate-800 px-3 py-1 rounded" data-testid="missing-tables-editor-link">Editörde çiz</Link>
                  <button type="button" disabled={ensuring} onClick={() => ensureMissingTables(floorMissing.floor_id, floorMissing.numbers)}
                    className="text-xs font-bold text-slate-900 bg-red-300 hover:bg-red-200 disabled:opacity-50 px-3 py-1 rounded" data-testid="missing-tables-create-btn">
                    {ensuring ? "Ekleniyor…" : `${floorMissing.numbers.length} masayı şemaya ekle`}
                  </button>
                </div>
              </div>
            )}
            {pickBooking && (
              <div className="mb-3 mx-auto max-w-[920px] flex items-center justify-between gap-3 bg-sky-500/15 border border-sky-500/40 rounded-lg px-4 py-2" data-testid="pick-banner">
                <div className="text-sm text-sky-200 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-sky-400 animate-pulse" />
                  <b>{pickBooking.customer_name || "Rezervasyon"}</b> · {pickBooking.total_pax || 0} kişi seçili — yerleştirmek için bir masaya veya dinamik bölgeye tıklayın
                </div>
                <button type="button" onClick={() => setPickBooking(null)} className="text-xs font-semibold text-slate-300 hover:text-white bg-slate-800 px-3 py-1 rounded" data-testid="pick-cancel">Vazgeç</button>
              </div>
            )}
            {currentFloor ? (
              <div style={{ width: WLOGICAL * zoom, height: HLOGICAL * zoom }} className="mx-auto">
              <div className="relative rounded-xl border border-slate-800 overflow-hidden"
                style={{ width: WLOGICAL, height: HLOGICAL, transform: `scale(${zoom})`, transformOrigin: "0 0", background: "radial-gradient(circle at 50% 30%, #0F1B2E 0%, #0B0F17 70%)" }} data-testid="floor-canvas">

                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 text-xs font-bold text-slate-500 uppercase tracking-[0.2em] flex items-center gap-2">
                  {currentFloor.floor_type === "VIP" && <Crown className="w-3.5 h-3.5 text-amber-400" />}{currentFloor.name}
                </div>

                {/* Bölgeler: dinamik VEYA sabit üye masası olan (drop/click hedefi) */}
                {floorLanes.filter(l => l.dynamic || tables.some(t => t.lane_id === l.id)).map(l => {
                  const zs = getZone(l.zone);
                  const used = zonePax(l.id);
                  const memberCap = tables.filter(t => t.lane_id === l.id && !t.auto_created).reduce((s, t) => s + (t.capacity || 0), 0);
                  const capShown = l.capacity || memberCap;
                  const over = capShown > 0 && used > capShown;
                  return (
                    <div key={`zone-${l.id}`}
                      data-zoneid={l.id}
                      onClick={() => pickBooking && seatInZone(pickBooking, l.id)}
                      className={`absolute rounded-xl z-0 ${pickBooking ? "cursor-pointer" : ""}`}
                      style={{
                        left: l.x, top: l.y, width: l.width, height: l.height,
                        background: `linear-gradient(160deg, ${zs.color}22, ${zs.color}0a)`,
                        border: `2px dashed ${zs.color}${pickBooking ? "" : "aa"}`,
                        outline: pickBooking ? `2px solid ${zs.color}` : "none", outlineOffset: 2,
                      }}
                      data-testid={`op-zone-${l.id}`}>
                      <div className="absolute -top-3 left-2 flex items-center gap-1.5">
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider shadow" style={{ background: zs.color, color: "#0B0F17" }}>{l.name}</span>
                        <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold shadow ${over ? "bg-red-500 text-white" : "bg-slate-800 text-slate-200"}`}>{used}/{capShown} kişi</span>
                        {Object.entries(l.quotas || {}).filter(([, q]) => q !== null && q !== "").map(([k, q]) => {
                          const u = zoneNeedUsed(l.id, k);
                          return (
                            <span key={k} className={`px-1.5 py-0.5 rounded-md text-[9px] font-bold shadow ${u > Number(q) ? "bg-red-500 text-white" : "bg-slate-800 text-slate-300"}`} data-testid={`op-zone-quota-${l.id}-${k}`}>
                              {NEED_LABEL[k]} {u}/{q}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {floorElements.map(el => renderElement(el))}

                {floorTables.map(t => {
                  const zs = getZone(t.zone);
                  const asgs = tableAssignMap[t.id] || [];
                  const isOccupied = asgs.length > 0;
                  const isDropping = dropTarget === t.id;
                  const cap = t.capacity ?? 0;
                  const seated = asgs.reduce((s, a) => s + (a?.booking?.total_pax || 0), 0);
                  const remaining = cap - seated;
                  const overCap = seated > cap;
                  const isFull = remaining === 0;
                  // Durum rengi: boş=slate, kısmen=zone, tam=yeşil, aşım=kırmızı
                  const stateColor = overCap ? "#DC2626" : isFull ? "#22C55E" : isOccupied ? zs.color : "#37475F";
                  return (
                    <div key={t.id}
                      data-tableid={t.id}
                      onClick={() => pickBooking && seatPickedOnTable(t.id)}
                      className={`absolute flex flex-col items-center group transition-transform duration-200 hover:scale-[1.03] ${isDropping ? "z-40 scale-105" : "z-10 hover:z-30"} ${pickBooking ? "cursor-pointer" : ""}`}
                      style={{ left: t.x, top: t.y, transform: t.rotation ? `rotate(${t.rotation}deg)` : undefined, touchAction: "none", ...(pickBooking && !isFull ? { outline: "2px dashed rgba(56,189,248,0.55)", outlineOffset: "3px", borderRadius: 12 } : {}) }}
                      data-testid={`op-table-${t.id}`}>
                      {/* Durum etiketi */}
                      {isOccupied && (
                        <div className="flex items-center mb-0.5">
                          <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${overCap ? "bg-red-500/25 text-red-200" : isFull ? "bg-emerald-500/25 text-emerald-200" : "bg-sky-500/20 text-sky-200"}`}>
                            {overCap ? `AŞIM +${-remaining}` : isFull ? "DOLU" : `${remaining} boş`}
                          </span>
                        </div>
                      )}
                      {/* Gerçekçi masa görünümü (ortak RealTable bileşeni — Şema Editörü ile aynı) */}
                      {(() => {
                        const edge = isDropping ? "#22D3EE" : stateColor;
                        const bg = isDropping ? "rgba(34,211,238,0.14)" : `linear-gradient(160deg, ${stateColor}26, #131B2E 70%)`;
                        const glow = overCap ? "0 0 16px rgba(220,38,38,0.5)" : isFull ? "0 0 14px rgba(34,197,94,0.45)" : isDropping ? "0 0 22px rgba(34,211,238,0.55)" : "0 4px 12px rgba(0,0,0,0.45)";
                        return (
                          <RealTable
                            rotation={t.rotation || 0}
                            number={t.number}
                            capacity={cap}
                            edgeColor={edge}
                            bodyBg={bg}
                            bodyBorderStyle={isOccupied ? "solid" : "dashed"}
                            glow={glow}
                            numberSize={18}
                            sublabel={isOccupied ? `${seated}/${cap || "x"}` : (cap ? `${cap} kişi` : "x")}
                            sublabelColor={edge}
                            filledCount={seated}
                            chairFilledColor={stateColor}
                            chairEmptyBorderColor="#475569"
                            chairBorder={true}
                          />
                        );
                      })()}
                      {isDropping && <div className="text-[9px] font-bold text-cyan-300 animate-pulse whitespace-nowrap mt-0.5">↓ Bırak</div>}
                      {/* Rezervasyon çipleri (sürüklenebilir/kaldırılabilir) */}
                      {isOccupied && (
                        <div className="flex flex-col gap-0.5 mt-1" style={{ minWidth: 90, maxWidth: 220 }}>
                          {asgs.map(a => {
                            const bk = a.booking || {};
                            const nm = (bk.customer_name || "").split(" ").slice(0, 2).join(" ");
                            return (
                              <div key={a.id}
                                onPointerDown={(e) => { e.stopPropagation(); startDrag(e, bk, t.id, a.id); }}
                                className="bg-slate-900/85 border border-slate-700 rounded px-1.5 py-0.5 cursor-grab active:cursor-grabbing hover:bg-slate-800"
                                style={{ touchAction: "none" }}
                                data-testid={`table-booking-${a.id}`}>
                                <div className="flex items-center justify-between gap-1">
                                  <span className="text-[9px] font-semibold text-slate-100 truncate">{nm}</span>
                                  <span className="flex items-center gap-1 shrink-0">
                                    <span className="text-[9px] font-mono font-bold" style={{ color: stateColor }}>{bk.total_pax || 0}</span>
                                    <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => removeAssignment(a.id)}
                                      className="text-slate-500 hover:text-red-400" data-testid={`remove-assign-${a.id}`}>
                                      <X className="w-2.5 h-2.5" />
                                    </button>
                                  </span>
                                </div>
                                {bk.tags && bk.tags.length > 0 && (
                                  <div className="flex items-center gap-0.5 flex-wrap mt-0.5">
                                    {bk.tags.map((tg, ti) => (
                                      <span key={ti} className="text-[7px] font-bold px-1 py-px rounded text-white leading-none" style={{ backgroundColor: tg.color }}>{tg.label}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}

                {floorTables.length === 0 && floorElements.length === 0 && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-600">
                    <Anchor className="w-16 h-16 mb-3 text-slate-700" />
                    <p className="text-sm font-medium text-slate-400">Bu katta masa tanımlanmamış</p>
                    <Link to="/table-schema" className="text-sky-400 text-xs hover:underline mt-1">Şema editöründen ekleyin →</Link>
                  </div>
                )}
              </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-500">
                <div className="text-center">
                  <Ship className="w-16 h-16 mx-auto mb-3 text-slate-700" />
                  <p className="text-sm">{boats.length === 0 ? "Tekne bulunamadı" : boatFloors.length === 0 ? "Bu tekneye kat tanımlanmamış" : "Bir kat seçin"}</p>
                  {boatFloors.length === 0 && <Link to="/table-schema" className="text-sky-400 text-xs hover:underline">Şema editörüne git →</Link>}
                </div>
              </div>
            )}
          </div>

          {/* Legend — viewport tabanında sabit (ölçeklenmez) */}
          {currentFloor && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-3 z-20 bg-slate-900/90 backdrop-blur-md px-4 py-1.5 rounded-full border border-slate-800 shadow-xl">
              {ZONES.map(z => (
                <div key={z.value} className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: z.color }} />
                  <span className="text-[10px] font-medium text-slate-400">{z.label}</span>
                </div>
              ))}
              <div className="w-px h-4 bg-slate-700" />
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded border-2 border-dashed border-slate-600" /><span className="text-[10px] text-slate-500">Boş</span></div>
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded" style={{ background: "#22C55E" }} /><span className="text-[10px] text-slate-500">Dolu</span></div>
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded" style={{ background: "#DC2626" }} /><span className="text-[10px] text-slate-500">Aşım</span></div>
            </div>
          )}
          </div>

          {/* === RIGHT: Bekleme Listesi + İstatistik === */}
          <div className="w-80 lg:w-96 bg-slate-900 border-l border-slate-800 flex flex-col shrink-0">
            <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
              <h3 className="text-lg font-semibold tracking-tight text-slate-100 flex items-center gap-2"><Clock className="w-4 h-4 text-amber-400" /> Bekleme Listesi</h3>
              <span className="text-sm font-black font-mono text-amber-300 bg-amber-500/15 px-2.5 py-0.5 rounded-full border border-amber-500/30">{waitingShown.length}{needFilter ? `/${waiting.length}` : ""}</span>
            </div>
            <div className="px-3 py-2 border-b border-slate-800 flex items-center gap-1 flex-wrap" data-testid="waiting-need-filter">
              <button type="button" onClick={() => setNeedFilter("")}
                className={`px-2 py-0.5 rounded-full text-[10px] font-bold border transition-colors ${!needFilter ? "bg-slate-100 text-slate-900 border-slate-100" : "bg-slate-800 text-slate-300 border-slate-700 hover:border-slate-500"}`} data-testid="need-filter-all">Tümü</button>
              {Object.keys(NEED_TAGS).map(k => {
                const n = waiting.filter(w => hasNeed(w.tags, k)).length;
                if (!n) return null;
                return (
                  <button key={k} type="button" onClick={() => setNeedFilter(needFilter === k ? "" : k)}
                    className={`px-2 py-0.5 rounded-full text-[10px] font-bold border transition-colors ${needFilter === k ? "bg-cyan-400 text-slate-900 border-cyan-400" : "bg-slate-800 text-slate-300 border-slate-700 hover:border-slate-500"}`} data-testid={`need-filter-${k}`}>
                    {NEED_TAGS[k][0]} <span className="font-mono">{n}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {waitingShown.map(w => {
                const zone = getZone(w.parsed?.zone);
                const isVip = w.parsed?.is_vip_menu;
                const isUncertain = !w.parsed?.zone;
                return (
                  <div key={w.id}
                    onPointerDown={(e) => startDrag(e, w)}
                    onClick={() => togglePick(w)}
                    style={{ touchAction: "pan-y" }}
                    className={`rounded-lg border p-3 cursor-pointer active:cursor-grabbing transition-all hover:border-slate-600 hover:-translate-y-0.5 shadow-lg ${pickBooking?.id === w.id ? "border-sky-400 ring-2 ring-sky-400/50 bg-sky-500/10" : isVip ? "border-amber-500/40 bg-amber-500/5" : isUncertain ? "border-red-500/30 bg-red-500/5" : "border-slate-800 bg-slate-900/80"}`}
                    data-testid={`waiting-card-${w.id}`}>
                    {pickBooking?.id === w.id && (
                      <div className="mb-1.5 text-[10px] font-bold text-sky-300 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse" /> Seçildi — bir masaya tıkla</div>
                    )}
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 mb-0.5">
                          {isVip && <span className="text-[9px] font-black text-amber-200 bg-amber-900/60 px-1.5 py-0.5 rounded flex items-center gap-0.5"><Crown className="w-2.5 h-2.5" />VIP</span>}
                          {isUncertain && <span className="text-[9px] font-black text-red-200 bg-red-900/60 px-1.5 py-0.5 rounded">BELİRSİZ</span>}
                        </div>
                        <div className="text-sm font-bold text-slate-100 truncate">{w.customer_name || "İsim yok"}</div>
                        <div className="text-[10px] text-slate-500">{w.hotel_name || (isVip ? "VIP Menü" : "Standart Menü")}</div>
                        {w.live_pos && (
                          <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-red-200 bg-red-900/50 border border-red-500/40 px-1.5 py-0.5 rounded" data-testid={`waiting-live-pos-${w.id}`} title="Canlı Defter'de bu masa yazılı ama şemada çizili değil">
                            <AlertTriangle className="w-3 h-3" /> Canlı: {w.live_pos.masa_no} / {w.live_pos.alt_no} — {w.live_pos.reason === "kat_yok" ? "kat yok" : "şemada yok"}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0 ml-2">
                        <Users className="w-3.5 h-3.5 text-slate-500" />
                        <span className="text-base font-black font-mono text-slate-200">{w.total_pax || 0}</span>
                      </div>
                    </div>
                    {(w.tags && w.tags.length > 0) ? (
                      <div className="flex items-center gap-1 flex-wrap mb-2" data-testid={`waiting-tags-${w.id}`}>
                        {w.tags.map((tg, ti) => (
                          <span key={ti} className="text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: tg.color }}>{tg.label}</span>
                        ))}
                      </div>
                    ) : (w.parsed?.zone && (
                      <div className="flex items-center gap-1 flex-wrap mb-2">
                        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ color: zone.badgeText, backgroundColor: zone.badgeBg }}>{zone.label}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-slate-600 font-mono">#{w.confirmation_code || w.bokun_booking_id}</span>
                      <div className="flex items-center gap-1"><Sparkles className="w-3 h-3 text-amber-400" /><span className="text-sm font-black font-mono text-amber-400">{w.score || 0}</span></div>
                    </div>
                    {isUncertain && (
                      <div className="mt-2 text-center text-[10px] text-red-400 flex items-center justify-center gap-1"><GripVertical className="w-3 h-3" /> Sürükleyerek elle yerleştir</div>
                    )}
                  </div>
                );
              })}
              {waiting.length === 0 && (
                <div className="text-center py-10 text-slate-600"><Check className="w-8 h-8 mx-auto mb-2 text-emerald-500" /><p className="text-xs">Tüm rezervasyonlar yerleşti</p></div>
              )}
            </div>

            {/* Doluluk breakdown */}
            <div className="p-4 border-t border-slate-800 bg-slate-950/50">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Bölge Doluluğu</h3>
              {ZONES.map(z => {
                const s = zoneStats[z.value];
                if (!s || s.total === 0) return null;
                const pct = Math.round(s.occupied / s.total * 100);
                return (
                  <div key={z.value} className="mb-2.5">
                    <div className="flex justify-between text-xs mb-1"><span className="font-medium" style={{ color: z.color }}>{z.label}</span><span className="font-black font-mono" style={{ color: z.color }}>{s.occupied}/{s.total}</span></div>
                    <div className="w-full bg-slate-800 rounded-full h-1.5"><div className="rounded-full h-1.5 transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: z.color }} /></div>
                  </div>
                );
              })}
              <div className="grid grid-cols-2 gap-2 mt-3">
                <button type="button" className="flex items-center justify-center gap-1.5 p-2 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors border border-slate-700 text-slate-300" data-testid="auto-assign-btn"><Sparkles className="w-4 h-4 text-amber-400" /><span className="text-[11px] font-medium">Otomatik</span></button>
                <button type="button" onClick={() => fetchData(true)} className="flex items-center justify-center gap-1.5 p-2 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors border border-slate-700 text-slate-300" data-testid="quick-refresh"><RotateCcw className="w-4 h-4 text-emerald-400" /><span className="text-[11px] font-medium">Yenile</span></button>
              </div>
              {lastUpdate && <div className="text-[10px] text-slate-600 mt-2 text-center flex items-center justify-center gap-1"><Clock className="w-3 h-3" /> Son: {format(lastUpdate, "HH:mm:ss")}</div>}
            </div>
          </div>
        </div>
      )}

      {/* Sürüklenen hayalet kart (fare + dokunmatik) */}
      {drag && drag.active && (
        <div className="fixed z-[60] pointer-events-none -translate-x-1/2 -translate-y-1/2 px-3 py-2 rounded-lg bg-sky-500 text-white text-xs font-bold shadow-2xl ring-2 ring-white/30 flex items-center gap-1.5"
          style={{ left: drag.x, top: drag.y }} data-testid="drag-ghost">
          <Users className="w-3.5 h-3.5" /> {drag.booking?.customer_name || "Rezervasyon"} · {drag.booking?.total_pax || 0}
        </div>
      )}

      {/* Drag hint banner */}
      {!loading && drag && drag.active && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-sky-500/90 backdrop-blur text-white text-xs font-semibold px-4 py-2 rounded-full shadow-xl flex items-center gap-2">
          <Info className="w-4 h-4" /> <span className="font-bold">{drag.booking?.customer_name}</span> — {drag.fromAssignmentId ? "başka masaya taşıyın veya masayla değiştirin" : "uygun bir masaya bırakın"}
        </div>
      )}

      {/* === VALIDATION MODAL === */}
      {validationModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="validation-modal">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-2 mb-4"><AlertTriangle className="w-6 h-6 text-amber-400" /><h3 className="text-lg font-bold text-slate-100">Yerleşim Uyarısı</h3></div>
            <div className="space-y-2 mb-5">
              {validationModal.validation.warnings.map((w, i) => (
                <div key={i} className={`flex items-start gap-2 p-3 rounded-lg text-sm ${w.type === "error" ? "bg-red-900/40 text-red-200 border border-red-700/50" : "bg-amber-900/30 text-amber-200 border border-amber-700/50"}`}>
                  {w.type === "error" ? <X className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}{w.message}
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setValidationModal(null)} className="flex-1 py-2.5 bg-slate-800 text-slate-200 rounded-xl font-semibold hover:bg-slate-700 transition-colors" data-testid="cancel-assign-btn">İptal</button>
              <button type="button" onClick={forceAssign} className="flex-1 py-2.5 bg-amber-500 text-slate-900 rounded-xl font-bold hover:bg-amber-400 transition-colors" data-testid="force-assign-btn">Yine de Ata</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
